import { differenceInCalendarDays } from "date-fns";
import { ConnectProto } from "@racoon/api";
import { getEnumName, parseLocalDateParam, startOfLocalDay, toDateParam } from "@racoon/lib";

type Schedule = ConnectProto.ContainerGroupSchedule;

/** "JOB_TYPE_*" name of a schedule's job type. */
export const scheduleJobType = (schedule: Schedule): string =>
  getEnumName(ConnectProto.JobTypeSchema, schedule.jobType) ?? "";

/**
 * Whether the generator reads the row: a mirror row copied from a legacy column key ("MSW", "10")
 * arrives with job type UNDEFINED, which no generated job matches.
 */
export const scheduleReadable = (schedule: Schedule): boolean =>
  schedule.jobType !== ConnectProto.JobType.UNDEFINED;

/**
 * The division serving a zoned group's schedule, as the generator decides: its own while that is in
 * the zone, else the zone's first; undefined without a zone (the contract's provider serves).
 */
export const scheduleDivisionId = (schedule: Schedule, zoneIds: string[]): string | undefined => {
  if (zoneIds.length === 0) return undefined;
  const own = String(schedule.serviceProviderId ?? "");
  return zoneIds.includes(own) ? own : zoneIds[0];
};

/** Schedule rows the generator reads, by stringified container group id. */
export const schedulesByCgId = (schedules: Schedule[]): Map<string, Schedule[]> => {
  const byCg = new Map<string, Schedule[]>();
  for (const schedule of schedules) {
    if (!scheduleReadable(schedule)) continue;
    const key = String(schedule.containerGroupId);
    byCg.set(key, [...(byCg.get(key) ?? []), schedule]);
  }
  return byCg;
};

/** "YYYY-MM-DD" a schedule row ends on, inclusive: valid_to, or a legacy row's own range; "" when open-ended. */
export const scheduleEndDate = (schedule: Schedule): string =>
  schedule.validTo || schedule.legacyExpr?.split("|")[2]?.trim() || "";

/** Today as "YYYY-MM-DD", the key every "still in effect" check compares against. */
export const todayKey = (): string => toDateParam(new Date());

/** Whether a schedule row still serves on or after `day` ("YYYY-MM-DD"). */
export const scheduleLive = (schedule: Schedule, day: string): boolean => {
  const end = scheduleEndDate(schedule);
  return !end || end >= day;
};

/** Distinct job types a container group's schedule rows are for. */
export const scheduledJobTypes = (schedules: Schedule[] = []): string[] => [
  ...new Set(schedules.map(scheduleJobType).filter(Boolean)),
];

const weekOfMonth = (date: Date) => Math.ceil(date.getDate() / 7);
const inMonths = (months: number[], date: Date) =>
  months.length === 0 || months.includes(date.getMonth() + 1);

/**
 * Whether a schedule row puts a pickup on the calendar day of `date`, as the
 * backend generator evaluates it: inside the period and matched by the typed
 * rules. A legacy row is read from its cron string.
 */
export const scheduleCoversDate = (schedule: Schedule, date: Date): boolean => {
  const day = toDateParam(date);
  if (schedule.validFrom && day < schedule.validFrom) return false;
  if (schedule.validTo && day > schedule.validTo) return false;
  if (schedule.legacyExpr) return legacyExpressionCoversDate(schedule.legacyExpr, date);
  const rule = schedule.rules?.rule;
  switch (rule?.case) {
    case "weekly":
      return (
        inMonths(rule.value.months, date) &&
        rule.value.days.some(
          (d) =>
            d.dow === date.getDay() &&
            (d.weeks.length === 0 || d.weeks.includes(weekOfMonth(date))),
        )
      );
    case "monthly":
      return (
        inMonths(rule.value.months, date) && rule.value.days.some((d) => d.day === date.getDate())
      );
    case "everyNDays":
      // Counted from valid_from, which the period check above has already reached.
      return (
        !!schedule.validFrom &&
        rule.value.n > 0 &&
        differenceInCalendarDays(date, parseLocalDateParam(schedule.validFrom) ?? date) %
          rule.value.n ===
          0
      );
    default:
      return false;
  }
};

/**
 * Days of week (0 = Sunday) a typed schedule serves on every week of every month;
 * a weekday limited to some weeks or months is left out, so weekday totals are not
 * overcounted. Empty for monthly rules and for every N days unless N divides a week.
 * Legacy rows are not read here.
 */
export const scheduleWeekdays = (schedule: Schedule): number[] => {
  const rule = schedule.rules?.rule;
  switch (rule?.case) {
    case "weekly": {
      if (rule.value.months.length > 0) return [];
      const { days } = rule.value;
      // A weekday counts once its entries together cover every week (no weeks listed = every week).
      const everyWeek = (dow: number) => {
        const entries = days.filter((d) => d.dow === dow);
        const weeks = new Set(entries.flatMap((d) => d.weeks));
        return (
          entries.some((d) => d.weeks.length === 0) || [1, 2, 3, 4, 5].every((w) => weeks.has(w))
        );
      };
      return [...new Set(days.map((d) => d.dow))].filter(everyWeek);
    }
    case "everyNDays": {
      const { n } = rule.value;
      if (!schedule.validFrom || n < 1 || 7 % n !== 0) return [];
      const first = (parseLocalDateParam(schedule.validFrom) ?? new Date()).getDay();
      return Array.from({ length: 7 / n }, (_, i) => (first + i * n) % 7);
    }
    default:
      return [];
  }
};

/**
 * Days of week (0 = Sunday) a typed schedule runs on at all, some weeks or months only included:
 * what its row shows. scheduleWeekdays is the stricter set totals count.
 */
export const scheduleDays = (schedule: Schedule): number[] => {
  const rule = schedule.rules?.rule;
  return rule?.case === "weekly"
    ? [...new Set(rule.value.days.map((d) => d.dow))]
    : scheduleWeekdays(schedule);
};

// --- Legacy cron expressions ------------------------------------------------
//
// Only legacy rows (legacy_expr) still carry one.
// A port of go-backend/internal/common/schedule (the grammar's source of
// truth) limited to what the frontend needs: whether an expression is active
// on a calendar day. Time windows are validated for shape but not evaluated.
//
//   "RULE(; RULE)*(| YYYY-MM-DD | YYYY-MM-DD)?"
//   RULE = "[HH:MM-HH:MM](,[HH:MM-HH:MM])* DOM MONTH DOW"
//
//   DOM    day of month mask: "*", "N", "N,M", "N-M" or mixes ("1-15,28")
//   MONTH  month mask (1-12), same forms
//   DOW    day of week mask (0=Sunday .. 6=Saturday), same forms
//   | a | b  optional validity range shared by all rules, inclusive

/** A calendar-component mask: every value, or an explicit set. */
type Mask = { any: true } | { any: false; set: Set<number> };

type Rule = {
  dayOfMonth: Mask;
  month: Mask;
  dayOfWeek: Mask;
};

type ParsedSchedule = {
  rules: Rule[];
  /** Inclusive, as local calendar days at midnight. */
  validFrom?: Date;
  validTo?: Date;
};

const maskMatches = (mask: Mask, value: number): boolean => mask.any || mask.set.has(value);

const parseInteger = (s: string): number | null => (/^\d+$/.test(s) ? Number(s) : null);

/** "*", "N", "N,M", "N-M" and mixes like "1-3,5"; null when malformed. */
const parseMask = (s: string, min: number, max: number): Mask | null => {
  if (s === "*") return { any: true };
  const set = new Set<number>();
  // Fails closed: a mask the Go parser would reject must cover no day, never
  // every day.
  for (const segment of s.split(",")) {
    const bounds = segment.trim().split("-");
    if (bounds.length > 2) return null;
    const [lo, hi = lo] = bounds.map(parseInteger);
    if (lo === null || hi === null || lo > hi || lo < min || hi > max) return null;
    for (let value = lo; value <= hi; value++) set.add(value);
  }
  return set.size === 0 ? null : { any: false, set };
};

// Digit runs on both sides, as Go's Atoi reads them: "6:5" is 06:05 there too.
const TIME_RE = /^(\d+):(\d+)$/;

/** "[H:MM-HH:MM]" segments, comma separated; only their shape matters here. */
const windowsWellFormed = (s: string): boolean => {
  const segments = s.split(",");
  if (segments.length === 0) return false;
  return segments.every((raw) => {
    const seg = raw.trim();
    if (!seg.startsWith("[") || !seg.endsWith("]")) return false;
    const bounds = seg.slice(1, -1).split("-");
    if (bounds.length !== 2) return false;
    const minutes = bounds.map((b) => {
      const match = TIME_RE.exec(b.trim());
      if (!match) return null;
      const h = Number(match[1]);
      const m = Number(match[2]);
      return h > 23 || m > 59 ? null : h * 60 + m;
    });
    const [start, end] = minutes;
    return start !== null && end !== null && end > start;
  });
};

const parseRule = (sub: string): Rule | null => {
  const fields = sub.trim().split(/\s+/);
  if (fields.length !== 4 || !windowsWellFormed(fields[0])) return null;
  const dayOfMonth = parseMask(fields[1], 1, 31);
  const month = parseMask(fields[2], 1, 12);
  const dayOfWeek = parseMask(fields[3], 0, 6);
  if (!dayOfMonth || !month || !dayOfWeek) return null;
  return { dayOfMonth, month, dayOfWeek };
};

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** "YYYY-MM-DD" as a local calendar day; null when malformed or not a real date. */
const parseDate = (s: string): Date | null => {
  const trimmed = s.trim();
  // The format check comes first: parseLocalDateParam falls back to `new Date(raw)`
  // for anything else, which would accept dates Go's time.Parse rejects.
  if (!DATE_RE.test(trimmed)) return null;
  return parseLocalDateParam(trimmed) ?? null;
};

/** Parses a service schedule expression; null for anything off the grammar. */
const parseServiceSchedule = (expression: string): ParsedSchedule | null => {
  const parts = expression.split("|");
  const parsed: ParsedSchedule = { rules: [] };
  if (parts.length === 3) {
    const validFrom = parseDate(parts[1]);
    const validTo = parseDate(parts[2]);
    if (!validFrom || !validTo || validTo < validFrom) return null;
    parsed.validFrom = validFrom;
    parsed.validTo = validTo;
  } else if (parts.length !== 1) {
    return null;
  }
  for (const sub of parts[0].split(";")) {
    const rule = parseRule(sub);
    if (!rule) return null;
    parsed.rules.push(rule);
  }
  return parsed;
};

/**
 * Whether the schedule is active on the calendar day of `date`: inside the
 * validity range and matched by at least one rule. A malformed expression
 * covers no day, as the daily job generator skips it too.
 */
export const legacyExpressionCoversDate = (expression: string, date: Date): boolean => {
  const parsed = parseServiceSchedule(expression);
  if (!parsed) return false;
  const day = startOfLocalDay(date);
  if (parsed.validFrom && day < parsed.validFrom) return false;
  if (parsed.validTo && day > parsed.validTo) return false;
  return parsed.rules.some(
    (rule) =>
      maskMatches(rule.dayOfMonth, day.getDate()) &&
      maskMatches(rule.month, day.getMonth() + 1) &&
      maskMatches(rule.dayOfWeek, day.getDay()),
  );
};
